// src/pages/marketplace/admin/AdminVerificationPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import type { MarketplaceMetadata } from '@core/types/product';
import {
  Button,
  Textarea,
  Card,
  CardContent,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
interface ChecklistState {
  legal_entity: boolean;
  functional_check: boolean;
  no_malicious: boolean;
  documentation: boolean;
  support_channel: boolean;
  tos_compliant: boolean;
}

const EMPTY_CHECKLIST: ChecklistState = {
  legal_entity: false,
  functional_check: false,
  no_malicious: false,
  documentation: false,
  support_channel: false,
  tos_compliant: false,
};

const CHECKLIST_LABELS: Record<keyof ChecklistState, string> = {
  legal_entity: 'Legal entity verified',
  functional_check: 'Product actually works (functional check)',
  no_malicious: 'No malicious code / behavior',
  documentation: 'Documentation is adequate',
  support_channel: 'Support channel is responsive',
  tos_compliant: 'Terms of service compliant',
};

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return String(dateStr);
  }
}

/**
 * Product verification workflow for the marketplace.
 * Route: /admin/products/verification
 */
export const AdminVerificationPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();

  const [pendingProducts, setPendingProducts] = useState<any[]>([]);
  const [verifiedProducts, setVerifiedProducts] = useState<any[]>([]);
  const [checklists, setChecklists] = useState<Record<string, ChecklistState>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    // Load all products and filter by verification status
    await store.ListProducts({
      pagination: {
        cursor: '',
        limit: 50,
        direction: 'forward',
      },
    });

    if (store.items) {
      const allProducts = store.items;

      // Pending = published (ACTIVE/BETA) but not verified
      const pending = allProducts.filter(
        (p: any) =>
          (p.status === 'ACTIVE' || p.status === 'BETA') &&
          !(p.is_verified || p.metadata?.is_verified),
      );

      // Recently verified = verified products, sorted by update time, limited to 10
      const verified = allProducts
        .filter((p: any) => p.is_verified || p.metadata?.is_verified)
        .sort((a: any, b: any) => {
          const dateA = new Date(a.updated_at || a.verified_at || 0).getTime();
          const dateB = new Date(b.updated_at || b.verified_at || 0).getTime();
          return dateB - dateA;
        })
        .slice(0, 10);

      setPendingProducts(pending);
      setVerifiedProducts(verified);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getChecklist = (productId: string): ChecklistState =>
    checklists[productId] || { ...EMPTY_CHECKLIST };

  const updateChecklist = (productId: string, field: keyof ChecklistState, value: boolean) => {
    setChecklists((prev) => ({
      ...prev,
      [productId]: {
        ...(prev[productId] || { ...EMPTY_CHECKLIST }),
        [field]: value,
      },
    }));
  };

  const getNote = (productId: string): string => notes[productId] || '';

  const updateNote = (productId: string, value: string) => {
    setNotes((prev) => ({
      ...prev,
      [productId]: value,
    }));
  };

  const isChecklistComplete = (productId: string): boolean => {
    const cl = getChecklist(productId);
    return Object.values(cl).every(Boolean);
  };

  const handleVerify = async (productId: string) => {
    setActionInProgress(productId);
    try {
      const patch: Partial<MarketplaceMetadata> = {
        is_verified: true,
        verified_at: new Date().toISOString(),
        metadata: { verification_notes: getNote(productId) },
      };
      await store.UpdateMarketplaceMetadata(productId, {
        marketplace: patch as MarketplaceMetadata,
      });
      await loadData();
    } catch {
      // Error handled by store
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRejectVerification = async (productId: string) => {
    const note = getNote(productId);
    if (!note.trim()) {
      // Require a note for rejection
      return;
    }
    setActionInProgress(productId);
    try {
      await store.SetProductStatus(productId, {
        status: 'PRODUCT_STATUS_DRAFT',
        reason: `Verification rejected: ${note}`,
      });
      await loadData();
    } catch {
      // Error handled by store
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRequestChanges = async (productId: string) => {
    const note = getNote(productId);
    if (!note.trim()) return;
    setActionInProgress(productId);
    try {
      // 'changes_requested' is not a field of MarketplaceMetadata in the regenerated API;
      // record the request in the free-form metadata map instead.
      const patch: Partial<MarketplaceMetadata> = {
        metadata: {
          changes_requested: 'true',
          changes_requested_notes: note,
          changes_requested_at: new Date().toISOString(),
        },
      };
      await store.UpdateMarketplaceMetadata(productId, {
        marketplace: patch as MarketplaceMetadata,
      });
      await loadData();
    } catch {
      // Error handled by store
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRevokeVerification = async (productId: string) => {
    if (!confirm('Are you sure you want to revoke verification for this product?')) return;
    setActionInProgress(productId);
    try {
      const patch: Partial<MarketplaceMetadata> = {
        is_verified: false,
        metadata: { verification_revoked_at: new Date().toISOString() },
      };
      await store.UpdateMarketplaceMetadata(productId, {
        marketplace: patch as MarketplaceMetadata,
      });
      await loadData();
    } catch {
      // Error handled by store
    } finally {
      setActionInProgress(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb and header */}
      <div>
        <nav className="text-sm mb-2">
          <Link to="/admin/products" className="text-muted-foreground hover:text-foreground">
            Admin
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to="/admin/products" className="text-muted-foreground hover:text-foreground">
            Products
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Verification</span>
        </nav>
      </div>

      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">Product Verification</h1>
      <p className="text-sm text-muted-foreground">
        {pendingProducts.length} product{pendingProducts.length !== 1 ? 's' : ''} awaiting
        verification
      </p>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Pending Verification */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Pending Verification</h2>

        {store.isLoading ? (
          <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>
        ) : pendingProducts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No products pending verification. All published products have been reviewed.
            </CardContent>
          </Card>
        ) : (
          pendingProducts.map((product: any) => {
            const productId = product.entity?.id || product.id;
            const checklist = getChecklist(productId);
            const note = getNote(productId);
            const allChecked = isChecklistComplete(productId);
            const isProcessing = actionInProgress === productId;

            return (
              <Card key={productId}>
                <CardContent className="p-5">
                  <div className="space-y-4">
                    {/* Product header */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex items-start gap-3">
                        {product.icon_url ? (
                          <img
                            src={product.icon_url}
                            alt=""
                            className="h-12 w-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
                            <span className="text-lg font-semibold text-muted-foreground">
                              {String(product.name ?? '').charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                        )}
                        <div>
                          <Link
                            to={`/admin/products/${productId}`}
                            className="text-base font-semibold text-foreground hover:text-primary"
                          >
                            {String(product.name ?? '')}
                          </Link>
                          <p className="text-sm text-muted-foreground">
                            by {String(product.owner?.name || product.owner || 'Unknown')}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Published {formatDate(product.published_at || product.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-3 text-sm">
                        <div className="text-center">
                          <p className="font-semibold text-foreground">
                            {product.install_count ?? product.metadata?.install_count ?? 0}
                          </p>
                          <p className="text-xs text-muted-foreground">Installs</p>
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-foreground">
                            {product.average_rating ?? product.metadata?.average_rating
                              ? `${product.average_rating ?? product.metadata?.average_rating}`
                              : '--'}
                          </p>
                          <p className="text-xs text-muted-foreground">Rating</p>
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-foreground">
                            {product.review_count ?? product.metadata?.review_count ?? 0}
                          </p>
                          <p className="text-xs text-muted-foreground">Reviews</p>
                        </div>
                      </div>
                    </div>

                    {/* Verification Checklist */}
                    <div className="border border-border rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-foreground mb-3">
                        Verification Checklist
                      </h4>
                      <div className="space-y-2">
                        {(Object.keys(CHECKLIST_LABELS) as Array<keyof ChecklistState>).map(
                          (key) => (
                            <label key={key} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={checklist[key]}
                                onChange={(e) => updateChecklist(productId, key, e.target.checked)}
                                className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
                              />
                              <span
                                className={`text-sm ${
                                  checklist[key] ? 'text-foreground' : 'text-muted-foreground'
                                }`}
                              >
                                {CHECKLIST_LABELS[key]}
                              </span>
                            </label>
                          ),
                        )}
                      </div>
                    </div>

                    {/* Admin Notes */}
                    <Textarea
                      label="Internal Admin Notes"
                      placeholder="Add notes about this verification review..."
                      value={note}
                      onChange={(e) => updateNote(productId, e.target.value)}
                      rows={3}
                    />

                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        variant="primary"
                        className="bg-green-600 hover:bg-green-700"
                        disabled={!allChecked || isProcessing}
                        onClick={() => handleVerify(productId)}
                      >
                        {isProcessing ? 'Processing...' : 'Verify'}
                      </Button>
                      <Button
                        variant="danger"
                        disabled={!note.trim() || isProcessing}
                        onClick={() => handleRejectVerification(productId)}
                      >
                        Reject Verification
                      </Button>
                      <Button
                        variant="secondary"
                        className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
                        disabled={!note.trim() || isProcessing}
                        onClick={() => handleRequestChanges(productId)}
                      >
                        Request Changes
                      </Button>
                      {!allChecked && (
                        <p className="text-xs text-muted-foreground self-center ml-2">
                          Complete all checklist items to verify
                        </p>
                      )}
                      {!note.trim() && (
                        <p className="text-xs text-muted-foreground self-center ml-2">
                          A note is required for rejection or change requests
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Recently Verified */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Recently Verified</h2>

        <Card>
          <Table striped hoverable>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Verified By</TableHead>
                <TableHead>Verified At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {verifiedProducts.length === 0 ? (
                <TableEmpty colSpan={4} message="No verified products yet" />
              ) : (
                verifiedProducts.map((product: any) => {
                  const productId = product.entity?.id || product.id;
                  const isProcessing = actionInProgress === productId;

                  return (
                    <TableRow key={productId}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {product.icon_url ? (
                            <img
                              src={product.icon_url}
                              alt=""
                              className="h-8 w-8 rounded-md object-cover"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-md bg-muted flex items-center justify-center">
                              <span className="text-xs font-medium text-muted-foreground">
                                {String(product.name ?? '').charAt(0).toUpperCase() || '?'}
                              </span>
                            </div>
                          )}
                          <div>
                            <Link
                              to={`/admin/products/${productId}`}
                              className="text-sm font-medium text-foreground hover:text-primary"
                            >
                              {String(product.name ?? '')}
                            </Link>
                            <div className="flex items-center gap-1 mt-0.5">
                              <Badge variant="default" size="sm">
                                <span className="flex items-center gap-1">
                                  <svg
                                    className="h-2.5 w-2.5"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                  Verified
                                </span>
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-foreground">
                          {String(
                            product.verified_by?.name ||
                              product.metadata?.verified_by ||
                              'Admin',
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(
                            product.verified_at ||
                              product.metadata?.verified_at ||
                              product.updated_at,
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          disabled={isProcessing}
                          onClick={() => handleRevokeVerification(productId)}
                        >
                          {isProcessing ? 'Revoking...' : 'Revoke'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
});

export default AdminVerificationPage;
